import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Button, BUTTON_TYPES } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import type {
  ImproveInterviewFeedbackCommentPayload,
  ImproveInterviewFeedbackCommentResponse,
} from 'actions/jobActions';
import type { InterviewFeedbackGroup } from 'constants/interview';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import type { Uuid } from 'store/reducers/types';
import { FeedbackCreation } from './FeedbackCreation';
import { Feedback } from './Feedback';
import { getFeedbackFormInitialValues, getFeedbackGroupView, isFeedbackCardEditable } from './utils';
import type { FeedbackData, FeedbackFormValues } from './types';

interface FeedbackCardsProps {
  currentUserUuid?: Uuid;
  editUuid: string;
  feedbacks: FeedbackData[];
  isEditableGroup: boolean;
  onEdit: (uuid: string) => void;
  onEditCancel: () => void;
  onFinish: (values: FeedbackFormValues, feedbackId: Uuid) => void;
  onImproveComment: (
    body: ImproveInterviewFeedbackCommentPayload,
    signal: AbortSignal
  ) => Promise<ImproveInterviewFeedbackCommentResponse>;
  onPostFeedback: (data: Partial<FeedbackData>) => void;
  showAll: boolean;
}

const FeedbackCards: React.FC<FeedbackCardsProps> = ({
  currentUserUuid,
  editUuid,
  feedbacks,
  isEditableGroup,
  onEdit,
  onEditCancel,
  onFinish,
  onImproveComment,
  onPostFeedback,
  showAll,
}) => (
  <>
    {feedbacks.slice(0, showAll ? feedbacks.length : 1).map(feedback => {
      const canEditCard = isFeedbackCardEditable(feedback, isEditableGroup);
      const isOwnEmptyFeedbackOnCurrentStep =
        canEditCard && !feedback.comment && feedback.approver.uuid === currentUserUuid;
      const isEditing = canEditCard && feedback.uuid === editUuid;
      const showCreationForm = isOwnEmptyFeedbackOnCurrentStep || isEditing;

      return showCreationForm ? (
        <FeedbackCreation
          key={feedback.uuid}
          feedbackId={feedback.uuid as Uuid}
          isPosted={!!feedback.posted_at}
          approver={feedback.approver}
          initialValues={getFeedbackFormInitialValues(feedback, isEditing)}
          onCancel={isEditing && onEditCancel}
          onFinish={onFinish}
          onImproveComment={onImproveComment}
        />
      ) : (
        <Feedback
          key={feedback.uuid}
          feedback={feedback}
          onEdit={() => onEdit(feedback.uuid as string)}
          onPostFeedback={onPostFeedback}
          showEdit={canEditCard && feedback.approver.uuid === currentUserUuid}
        />
      );
    })}
  </>
);

interface FeedbackShowMoreProps {
  count: number;
  open: boolean;
  onToggle: () => void;
}

const FeedbackShowMore: React.FC<FeedbackShowMoreProps> = ({ count, open, onToggle }) => {
  const { t } = useTranslation();

  if (count <= 1) return null;

  return (
    <Button className={cn('all-feedbacks', { open })} type={BUTTON_TYPES.TEXT} onClick={onToggle}>
      {open ? t('translation:feedback.show_less') : t('translation:feedback.show_more')}
    </Button>
  );
};

interface FeedbackGroupProps {
  currentStepId?: string;
  currentUserUuid?: Uuid;
  editUuid: string;
  embedded?: boolean;
  group: InterviewFeedbackGroup;
  groupsCount: number;
  onEdit: (uuid: string) => void;
  onEditCancel: () => void;
  onFinish: (values: FeedbackFormValues, feedbackId: Uuid) => void;
  onImproveComment: (
    body: ImproveInterviewFeedbackCommentPayload,
    signal: AbortSignal
  ) => Promise<ImproveInterviewFeedbackCommentResponse>;
  onPostFeedback: (data: Partial<FeedbackData>) => void;
  onToggleDetails: () => void;
  onToggleShowAll: () => void;
  openDetails: boolean;
  readOnly?: boolean;
  showAllFeedbacks: boolean;
  showRoundTitle: boolean;
  stepId: Uuid;
}

export const FeedbackGroup: React.FC<FeedbackGroupProps> = ({
  currentStepId,
  currentUserUuid,
  editUuid,
  embedded,
  group,
  groupsCount,
  onEdit,
  onEditCancel,
  onFinish,
  onImproveComment,
  onPostFeedback,
  onToggleDetails,
  onToggleShowAll,
  openDetails,
  readOnly = false,
  showAllFeedbacks,
  showRoundTitle,
  stepId,
}) => {
  const { t } = useTranslation();
  const view = getFeedbackGroupView(group, {
    currentUserUuid,
    readOnly,
    currentStepId,
    stepId,
    groupsCount,
    showAllFeedbacks,
  });
  const cardsProps = {
    currentUserUuid,
    editUuid,
    isEditableGroup: view.isEditableGroup,
    onEdit,
    onEditCancel,
    onFinish,
    onImproveComment,
    onPostFeedback,
  };

  const showForm = embedded && view.hasForm;
  const showToggle = embedded && !view.hasForm;
  const showHistory = !embedded;

  if (showHistory && !view.visibleFeedbacks.length) return null;

  return (
    <div className="job-interview-feedback__group">
      {showForm && (
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD} className="job-interview-feedback__title">
          {t('translation:feedback.title')}
        </Typography.Title3>
      )}

      {showToggle && (
        <div
          className={cn('job-interview-feedback__toggle', {
            open: openDetails,
            'job-interview-feedback__toggle--completed': !view.incomplete,
          })}
        >
          <span>{t(view.incomplete ? 'translation:feedback.not_completed' : 'translation:feedback.completed')}</span>
          <button
            type="button"
            className="job-interview-feedback__chevron"
            aria-expanded={openDetails}
            onClick={onToggleDetails}
          >
            <Arrow />
          </button>
        </div>
      )}

      {showHistory && showRoundTitle && (
        <div className="job-interview-feedback__group-header">
          <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:interview.round_title', {
              number: group.sequence,
            } as Record<string, unknown>)}
          </Typography.Title3>
        </div>
      )}

      {showForm && <FeedbackCards {...cardsProps} feedbacks={view.visibleFeedbacks} showAll />}

      {showToggle && openDetails && (
        <div className="job-interview-feedback__details">
          {view.postedFeedbacks.length === 0 ? (
            <Typography.Text className="job-interview-feedback__none">{t('translation:feedback.none')}</Typography.Text>
          ) : (
            <>
              <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD} className="job-interview-feedback__count">
                {t('translation:feedback.count', {
                  posted: view.postedCount,
                  total: view.totalCount,
                } as Record<string, unknown>)}
              </Typography.Title3>
              <FeedbackCards {...cardsProps} feedbacks={view.postedFeedbacks} showAll={showAllFeedbacks} />
              <FeedbackShowMore
                count={view.postedFeedbacks.length}
                open={showAllFeedbacks}
                onToggle={onToggleShowAll}
              />
            </>
          )}
        </div>
      )}

      {showHistory && (
        <>
          <FeedbackCards {...cardsProps} feedbacks={view.visibleFeedbacks} showAll={view.showAll} />
          {groupsCount === 1 && (
            <FeedbackShowMore count={view.visibleFeedbacks.length} open={showAllFeedbacks} onToggle={onToggleShowAll} />
          )}
        </>
      )}
    </div>
  );
};
